import { inSubGroup, type NavigationEntry, type NavigationSubGroup } from "@positive/ui";
import "@ui5/webcomponents-icons/dist/sales-quote.js";
import "@ui5/webcomponents-icons/dist/sales-order.js";
import "@ui5/webcomponents-icons/dist/sales-order-item.js";
import "@ui5/webcomponents-icons/dist/shipping-status.js";
import "@ui5/webcomponents-icons/dist/sales-document.js";
import "@ui5/webcomponents-icons/dist/undo.js";
import "@ui5/webcomponents-icons/dist/request.js";
import "@ui5/webcomponents-icons/dist/lead-outdated.js";
import "@ui5/webcomponents-icons/dist/table-view.js";
import "@ui5/webcomponents-icons/dist/receipt.js";
import "@ui5/webcomponents-icons/dist/employee.js";
import "@ui5/webcomponents-icons/dist/customer.js";
import "@ui5/webcomponents-icons/dist/reset.js";
import "@ui5/webcomponents-icons/dist/discussion-2.js";
import "@ui5/webcomponents-icons/dist/accounting-document-verification.js";
import "@ui5/webcomponents-icons/dist/process.js";
import "@ui5/webcomponents-icons/dist/lead.js";
import "@ui5/webcomponents-icons/dist/target-group.js";

import { kindKey, kindPermission, listPaths, salesKinds, type SalesKind } from "./kinds";
import { salesPermissions } from "./permissions";

const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const kindSubGroup: Partial<Record<string, NavigationSubGroup>> = { priceCredit: "billing" };

const kindIcons: Record<SalesKind, string> = {
  quotations: "sales-quote",
  orders: "sales-order",
  "pick-lists": "sales-order-item",
  deliveries: "shipping-status",
  invoices: "sales-document",
  returns: "undo",
  "return-requests": "request",
  "price-credits": "lead-outdated",
};

/** Sales entries of the navigation registry (group `sales`, plan module `sales`). */
export const salesNavigation: readonly NavigationEntry[] = [
  ...salesKinds.map((kind) => ({
    key: `sales.${kindKey[kind]}`,
    ...inSubGroup(kindSubGroup[kindKey[kind]]),
    labelKey: `nav.sales${capital(kindKey[kind])}`,
    icon: kindIcons[kind],
    path: listPaths[kind],
    permission: kindPermission(kind, "view"),
  })),
  {
    key: "sales.billingDue",
    subGroup: "billing",
    labelKey: "nav.salesBillingDue",
    icon: "receipt",
    path: "/sales/billing-due",
    permission: salesPermissions.billingDueView,
  },
  {
    key: "sales.accruals",
    subGroup: "billing",
    labelKey: "nav.salesAccruals",
    icon: "process",
    path: "/sales/accruals",
    permission: salesPermissions.accrualView,
  },
  {
    key: "sales.commission",
    subGroup: "billing",
    labelKey: "nav.salesCommission",
    icon: "lead",
    path: "/sales/commission",
    permission: salesPermissions.commissionView,
  },
  {
    key: "sales.targets",
    subGroup: "billing",
    labelKey: "nav.salesTargets",
    icon: "target-group",
    path: "/sales/targets",
    permission: salesPermissions.targetView,
  },
  {
    key: "sales.setup.salespeople",
    subGroup: "setup",
    labelKey: "nav.salesSalespeople",
    icon: "employee",
    path: "/sales/setup/salespeople",
    permission: salesPermissions.salespersonView,
  },
  {
    key: "sales.setup.customerProfiles",
    subGroup: "setup",
    labelKey: "nav.salesCustomerProfiles",
    icon: "customer",
    path: "/sales/setup/customer-profiles",
    permission: salesPermissions.customerProfileView,
  },
  {
    key: "sales.setup.promotions",
    subGroup: "setup",
    labelKey: "nav.salesPromotions",
    icon: "discussion-2",
    path: "/sales/setup/promotions",
    permission: salesPermissions.promotionView,
  },
  {
    key: "sales.setup.returnReasons",
    subGroup: "setup",
    labelKey: "nav.salesReturnReasons",
    icon: "reset",
    path: "/sales/setup/return-reasons",
    permission: salesPermissions.returnReasonView,
  },
  {
    key: "sales.setup.accounts",
    subGroup: "setup",
    labelKey: "nav.salesAccountSettings",
    icon: "accounting-document-verification",
    path: "/sales/setup/accounts",
    permission: salesPermissions.accountSettingsView,
  },
  {
    key: "sales.setup.discountLimits",
    subGroup: "setup",
    labelKey: "nav.salesDiscountLimits",
    icon: "lead-outdated",
    path: "/sales/setup/discount-limits",
    permission: salesPermissions.settingsView,
  },
  {
    key: "sales.report.salesAnalysis",
    subGroup: "reports",
    labelKey: "nav.salesReportSalesAnalysis",
    icon: "table-view",
    path: "/sales/reports/sales-analysis",
    permission: salesPermissions.salesAnalysisView,
  },
  {
    key: "sales.report.openOrders",
    subGroup: "reports",
    labelKey: "nav.salesReportOpenOrders",
    icon: "table-view",
    path: "/sales/reports/open-orders",
    permission: salesPermissions.openOrdersView,
  },
];

/** Receivables reports (group `receivables`, plan module `finance`). */
export const receivablesNavigation: readonly NavigationEntry[] = [
  {
    key: "ar.aging",
    labelKey: "nav.arAging",
    icon: "table-view",
    path: "/ar/reports/aging",
    permission: salesPermissions.agingView,
  },
  {
    key: "ar.creditExposure",
    labelKey: "nav.arCreditExposure",
    icon: "table-view",
    path: "/ar/reports/credit-exposure",
    permission: salesPermissions.creditExposureView,
  },
];
